import React, { useState, useContext, useEffect } from "react";
import { Button, Input, Label } from "reactstrap";
import { useHistory } from "react-router-dom";
import PsychometricService from "../../services/PsychometricService";
import { ThemeContext } from "../../contexts/ThemeContext";
import { PsychometricContext } from "../../contexts/PsychometricContext";
import PsychometricTestMainNavbar from './PsychometricTestMainNavbar';
import ErrorBoundary from "../general/ErrorBoundary";
import Message from '../general/Message';

const PsychometricIndividualTestTakerPortal = () => {
  const history = useHistory();
  const { theme } = useContext(ThemeContext);
  const {
    setSignedEmail,
    setIsOrganization,
    setSignedFirstName,
    setSignedLastName,
    setSignedID,
    setSignedOrganization,
    setSignedMobile,
    setSignedPassword,
    setIsStudent
  } = useContext(PsychometricContext);

  const [individualUser, setIndividualUser] = useState({ 
    email: "", 
    password: "",
    otp: "" 
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ msgBody: '', msgError: false });
  const [isTwoFactorOn, setIsTwoFactorOn] = useState(false);
  const [showPhoneOTP, setShowPhoneOTP] = useState(false);

  useEffect(() => {
    setSignedEmail(null);
    setIsOrganization(null);
    setSignedFirstName(null);
    setSignedLastName(null);
    setSignedID(null);
    setSignedOrganization(null);
    setSignedMobile(null);
    setSignedPassword(null);
    setIsStudent(null);
    
    localStorage.removeItem('signedEmail');
    localStorage.removeItem('isOrganization');
    localStorage.removeItem('signedFirstName');
    localStorage.removeItem('signedLastName');
    localStorage.removeItem('signedId');
    localStorage.removeItem('signedOrganization');
    localStorage.removeItem('signedMobile');
    localStorage.removeItem('signedPassword');
    localStorage.removeItem('isStudent');
    
    sessionStorage.removeItem('psychometricTestStep');
    sessionStorage.removeItem('psychometricTestInProgress');
  }, []);

  const handleIndividualChange = (e) => {
    setIndividualUser({ ...individualUser, [e.target.name]: e.target.value });
  };

  const handleIndividualSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { email, password } = individualUser;
      
      // First check if test is already completed
      const completionStatus = await PsychometricService.checkTestCompletion(
        null, null, null, null, email
      );

      if (completionStatus.hasCompletedTest) {
        setMessage({ 
          msgBody: "You have already completed the test. You cannot take it again.", 
          msgError: true 
        });
        setIsLoading(false);
        return;
      }

      // First check if user exists
      const nameresponse = await PsychometricService.getPsychometricIndividual(email);
      if (!nameresponse) {
        setMessage({ msgBody: "User does not exist. Please check your credentials or sign up below to create an account", msgError: true });
        setIsLoading(false);
        return;
      }

      const response = await PsychometricService.signInIndividual(email, password);

      if (response?.isTwoFactorOn) {
        setIsTwoFactorOn(true);
        setMessage({ msgBody: "Please check your email for the OTP code", msgError: false });
        setIsLoading(false);
        return;
      }

      if (response && nameresponse) {
        setSignedEmail(email);
        setSignedPassword(password);
        setSignedFirstName(nameresponse.user.firstName);
        setSignedLastName(nameresponse.user.lastName);
        setIsStudent(true);
        setIsOrganization(false);

        setMessage({ msgBody: "Individual authenticated successfully", msgError: false });
        history.push(`/psychometrictest/${email}`);
      } else {
        setMessage({ msgBody: "Invalid credentials", msgError: true });
      }
    } catch (error) {
      setMessage({ 
        msgBody: error?.response?.data?.message?.msgBody || "User does not exist. Please check your credentials or sign up below to create an account", 
        msgError: true 
      });
      console.error("Error authenticating individual:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { email, otp, password } = individualUser;



      const nameresponse = await PsychometricService.getPsychometricIndividual(email);
      const response = await PsychometricService.signInIndividual(email, password, {
        isTwoFactor: true,
        otp
      });

      if (response?.message === 'Sign-in successful' || response?.user) {
        setSignedEmail(email);
        setSignedPassword(password);
        setSignedFirstName(nameresponse.user.firstName);
        setSignedLastName(nameresponse.user.lastName);
        setIsStudent(true);
        setIsOrganization(false);

        setMessage({ msgBody: "Individual authenticated successfully", msgError: false });
        history.push(`/psychometrictest/${email}`);
      } else if (response?.isTwoFactorOn) {
        setMessage({ msgBody: "Invalid OTP code. Please try again.", msgError: true });
      } else {
        setMessage({ msgBody: response?.message || "Invalid OTP code. Please try again.", msgError: true });
      }
    } catch (error) {
      setMessage({ 
        msgBody: error?.message?.msgBody || "Error verifying OTP", 
        msgError: true 
      });
      console.error("Error verifying OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOTPToPhone = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
        const response = await PsychometricService.sendOTPToPhone(individualUser.email);
        setMessage({
            msgBody: "OTP sent to your registered mobile number",
            msgError: false
        });
    } catch (error) {
        if (error?.message?.isSMSError) {
            setMessage({
                msgBody: "SMS delivery failed. Please use email OTP instead.",
                msgError: true
            });
            // Reset the UI to show email OTP option
            setShowPhoneOTP(false);
        } else {
            setMessage({
                msgBody: error?.message?.msgBody || "Failed to send OTP to phone",
                msgError: true
            });
        }
    } finally {
        setIsLoading(false);
    }
  };

  return (
    <div>
      <ErrorBoundary errorAt="At Individual Test Taker Portal Navbar">
        <PsychometricTestMainNavbar />
      </ErrorBoundary>
      
      <div className="text-center mt-4">
        <h1 style={theme.headingXXL}>Individual Test Taker Portal</h1>
      </div>
      
      <div className="p-3 col-md-4 mt-5 mb-5 offset-md-4" style={theme.form}>
        <form 
          onSubmit={isTwoFactorOn ? handleOtpSubmit : handleIndividualSubmit} 
          style={{ paddingLeft: "10%", paddingRight: "10%" }}
        >
        <h2 id="login-heading" style={{ ...theme.heading, textAlign: "center" }}>
            Individual Test Taker 
          </h2>
          <h2 style={{ ...theme.heading, textAlign: "center" }}>
            Sign In
          </h2>
          <ErrorBoundary errorAt="At Individual Test Taker Portal Message">
            {message.msgBody ? <Message message={message} /> : null}
          </ErrorBoundary>
          <br />
          <Label htmlFor="orgEmail" style={theme.formBodyText}>
            Email
          </Label>
          <Input
            type="email"
            id="orgEmail"
            name="email"
            value={individualUser.email}
            onChange={handleIndividualChange}
            className="form-control"
            required
            style={theme.formInput}
          />
          <br />
          <Label htmlFor="password" style={theme.formBodyText}>
            Password
          </Label>
          <Input
            type="password"
            id="password"
            name="password"
            value={individualUser.password}
            onChange={handleIndividualChange}
            className="form-control"
            required
            style={theme.formInput}
          />
          {isTwoFactorOn && (
            <div>
              <Label htmlFor="otp" style={theme.formBodyText}>
                OTP
              </Label>
              <Input
                type="text"
                id="otp"
                name="otp"
                value={individualUser.otp}
                onChange={handleIndividualChange}
                className="form-control"
                required
                style={theme.formInput}
              />
              <br />
              {!showPhoneOTP ? (
                <Button
                  type="button"
                  color="secondary"
                  onClick={() => setShowPhoneOTP(true)}
                  style={{ ...theme.formSubmit, marginTop: '10px' }}
                >
                  Send OTP to Mobile Instead
                </Button>
              ) : (
                <Button
                  type="button"
                  color="secondary"
                  onClick={handleSendOTPToPhone}
                  disabled={isLoading}
                  style={{ ...theme.formSubmit, marginTop: '10px' }}
                >
                  {isLoading ? (
                    <span className="spinner-border spinner-border-sm mr-1"></span>
                  ) : null}
                  Send OTP to Mobile
                </Button>
              )}
            </div>
          )}
          <br />
          <div className="text-center mt-4">
          <Button
            type="submit"
            color="primary"
            disabled={isLoading}
            style={theme.formSubmit}
          >
            {isLoading ? (
              <span className="spinner-border spinner-border-sm mr-1"></span>
            ) : null}
            {isTwoFactorOn ? "Verify OTP" : "Sign In"}
          </Button>
          </div>
          <div className="text-end mb-3 mt-3 mt-md-4 mt-lg-5">
            <a href="/psychometricForgotPassword" style={{ color: '#006400' }}>Forgot Password?</a>
          </div>
          <div className="text-center mt-3 p-2" style={{ backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            <p style={{ ...theme.formBodyText, color: '#000000' }}>
              New to Psychometric Tests?{' '}
              <br />
              <a 
                href="/signup" 
                style={{ 
                  ...theme.link, 
                  textDecoration: 'underline', 
                  cursor: 'pointer',
                  color: '#28a745'  // Bootstrap's green color
                }}
                onClick={(e) => {
                  e.preventDefault();
                  history.push('/psychometricIndividualSignUp');
                }}
              >
                Sign up here
              </a>
            </p>
          </div>
        </form>
      </div>
      <div className="text-center mt-4">
      <Button
            type="button"
            color="secondary"
            style={theme.formSubmit}
            className="ml-2"
            onClick={() => history.push('/')}
          >
           Return to Home
          </Button>
      </div>
    </div>
  );
};

export default PsychometricIndividualTestTakerPortal;